
import React from "react";
import { i18nManager } from "../../i18n";
import Simulate from "../simulate/Simulate";
import { FormStep, Schemes, Utils } from "../../utils";
import { Svgs } from "../../constants/Images";
import ErrorSuccessView from "../ErrorSuccessView";
import { Button, Dropdown, FormControl, NumberInput, Scheme, TextInput } from "@rivtn/noseur";
import { PaymentMethod } from "../../constants/PaymentMethod";
import { ITransaction, ITransactionCharge, PloutousService, SanitizedResponse, WalletUssdBankProcessorOrMobileMoneyOperator } from "../../services";
import { Status } from "../../constants/Status";

export interface MobileMoneyProps {
    businessId: string;
    transaction: ITransaction;
    paymentSuccessful: () => void;
    loadingState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    fetchTransaction: (onDone?: (result?: ITransaction, error?: any) => void) => void;
}

function MobileMoney(props: MobileMoneyProps) {

    const transaction = props.transaction;
    const [_, setLoading] = props.loadingState;
    const ploutousService = PloutousService.getInstance();
    const labels = i18nManager.Labels.method.mobile_money;
    const [chargeData, setChargeData] = React.useState("");
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [serviceErrors, setServiceErrors] = React.useState<string[]>([]);
    const [serviceSuccesses, setServiceSuccesses] = React.useState<string[]>([]);
    const [serviceWarnings, setServiceWarnings] = React.useState<string[]>([]);
    const mobileMoneyCountry = labels.countries.find(c => c.code === transaction?.charge_country_code);
    const [selectedOperator, setSelectedOperator] = React.useState<WalletUssdBankProcessorOrMobileMoneyOperator>();
    const [step, setStep] = React.useState<"SELECT_OPERATOR" | "ENTER_MSISDN" | "CONFIRM_PAYMENT">("SELECT_OPERATOR");
    const countryAndCurrency = i18nManager.CountriesAndCurrencies.find(l => l.value === transaction.charge_country_code);
    const [msisdn, setMsisdn] = React.useState(Utils.sanitizeMobileNumber((transaction.msisdn ?? ""), mobileMoneyCountry?.dial_code));
    const operators: WalletUssdBankProcessorOrMobileMoneyOperator[] = props.transaction.payment_options.find(p => p.method === PaymentMethod.MOBILE_MONEY)?.momo_operators ?? [];

    React.useEffect(() => {
        if (!msisdn) {
            setServiceErrors(["The payer msisdn is missing for the transaction"]); // almost impossible
        }
    }, []);

    if (serviceSuccesses.length) {
        return Utils.buildPaymentCompletedView(transaction, serviceSuccesses);
    }

    if (serviceWarnings.length) {
        return Utils.buildPartialPaymentView(transaction, serviceWarnings, () => {
            setServiceWarnings([]);
            setStep("SELECT_OPERATOR");
        }, () => {
            setServiceWarnings([]);
            setStep(FormStep.CONFIRM_PAYMENT);
        });
    }

    if (serviceErrors.length) {
        return Utils.buildPaymentErrorView(transaction, serviceErrors, () => {
            setStep("SELECT_OPERATOR");
            setServiceErrors([]);
        });
    }

    return (<div className="momo credit-card">
        {step !== "CONFIRM_PAYMENT" && (<>
            <span className="title">{labels.title}</span>
            <div className="card-info" style={{ marginTop: 30 }}>
                <span className="title">{labels.choose_mobile_network}</span>
                <span className="desc">{labels.choose_mobile_network_desc}</span>
                <Dropdown fill scheme={Schemes.RIVTN_PLOUTOUS} placeholder={labels.select_mobile_network}
                    options={operators} optionMap={{
                        value: "code",
                        label: "name",
                        icon: "logo_location",
                    }} onSelectOption={(o: WalletUssdBankProcessorOrMobileMoneyOperator) => {
                        setSelectedOperator(o);
                        setStep("ENTER_MSISDN");
                        return true;
                    }} />
            </div>
            <div className="card-info">
                <span className="title">{labels.phone_number}</span>
                <span className="desc">{labels.phone_number_desc}</span>
                <FormControl fill leftContent={(<div className="momo-dial-code">
                    <img alt={mobileMoneyCountry?.dial_code} src={mobileMoneyCountry?.icon} />
                    <span>{mobileMoneyCountry?.dial_code}</span>
                </div>)}>
                    <TextInput fill name="gift_card_number" placeholder={mobileMoneyCountry?.placeholder}
                        defaultValue={msisdn} readOnly={!transaction.configuration?.can_change_msisdn}
                        disabled={step === "SELECT_OPERATOR"} onInput={(e: any) => setMsisdn(e.target.value)} />
                </FormControl>
            </div>
        </>)}
        {step === "CONFIRM_PAYMENT" && (<>
            <div className="instruction">
                <span>{labels.instruction}</span>
            </div>
            <Simulate currency={countryAndCurrency?.currency_sign} pages={{
                one: {
                    inputType: "PLAIN",
                    content: labels.enter_phone_number,
                    defaultInput: Utils.sanitizeMobileNumber(transaction.msisdn, mobileMoneyCountry?.dial_code, true),
                },
                two: {
                    inputType: "AMOUNT",
                    defaultInput: `${(transaction.amount / Utils.AMOUNT_UNIT)}`,
                    action: async (simulate: "SUCCESS" | "FAILURE", inputOne?: string, amount?: string) => {
                        try {
                            if (!inputOne || Utils.sanitizeMobileNumber(msisdn, mobileMoneyCountry?.dial_code, true) !== inputOne) {
                                throw new Error("The msisdn is incorrect");
                            }
                            const payload = {
                                simulate,
                                amount: parseFloat(amount ?? "-1"),
                            };
                            await ploutousService.simulateChargePayment(chargeData, payload);
                            Utils.toastSuccess(i18nManager.Labels.simulator.successful);
                        } catch (error: any) {
                            PloutousService.reportError(error);
                        }
                    }
                }
            }} />
        </>)}
        {errorMessages.map((errorMessage) => <span key={errorMessage} className="err">{errorMessage}</span>)}
        <Button raised fill scheme={Schemes.RIVTN_PLOUTOUS} className="pay"
            text={step !== "CONFIRM_PAYMENT" ? i18nManager.Labels.common.next : i18nManager.Labels.common.confirm_payment}
            disabled={!((step === "ENTER_MSISDN" && msisdn) || (step === FormStep.CONFIRM_PAYMENT))}
            onClick={proceedToPay} />
        {step === "CONFIRM_PAYMENT" && (<Button raised outlined fill scheme={Schemes.RIVTN_PLOUTOUS} className="pay"
            text={i18nManager.Labels.common.back} style={{ marginTop: 20 }} onClick={() => {
                setErrorMessages([]);
                setStep("ENTER_MSISDN");
            }} />)}
    </div>);

    async function proceedToPay() {
        setLoading(true);
        setErrorMessages([]);
        if (step === "ENTER_MSISDN") {
            ploutousService.initiateCharge(transaction, props.businessId, PaymentMethod.MOBILE_MONEY, {
                momo_operator_code: selectedOperator?.code,
                msisdn: (transaction.configuration.can_change_msisdn ? Utils.sanitizeMobileNumber(msisdn, mobileMoneyCountry?.dial_code, true) : undefined)
            }).then(({ sanitized }: SanitizedResponse<ITransactionCharge>) => {
                setChargeData(sanitized.extra_data?.charge_data);
                setStep(FormStep.CONFIRM_PAYMENT);
            }).catch((error: any) => {
                setServiceErrors(error.errors ?? [error.message]);
            }).finally(() => {
                setLoading(false);
            });
            return;
        }
        props.fetchTransaction((result?: ITransaction, error?: any) => {
            setLoading(false);
            if (error) {
                setServiceErrors(error.errors ?? [error.message]);
                return;
            }
            if ((result?.status ?? 0) === Status.TRANSACTION_PARTIALLY_PAID) {
                setServiceWarnings([i18nManager.Labels.common.partially_paid]);
                return;
            } else if ((result?.status ?? 0) > Status.TRANSACTION_FULLY_PAID_PENDING_ACKNOWLEDGEMENT) {
                props.paymentSuccessful();
                setServiceSuccesses([i18nManager.Labels.common.thank_you, i18nManager.Labels.common.payment_successful]);
                return;
            }
            //setConfirmingPayment(true);
            console.log("Initiate web socket connection to track payment completion");
        });
    }

}

export default MobileMoney;
